import React, { useCallback, useEffect, useState } from "react";
import { api, API_BASE } from "../../auth/api";
import TurnstileField from "../../components/TurnstileField";
import { LinkifiedText } from "./linkifyDescription";

const CATEGORY_LABELS = {
  firearms: "Firearms",
  ammunition: "Ammunition",
  parts: "Parts",
  accessories: "Accessories",
  belts: "Belts",
  services: "Services",
};

const FIREARM_TYPE_LABELS = {
  section_1_shotgun: "Section 1 Shotgun",
  section_2_shotgun: "Section 2 Shotgun",
  rifle: "Rifle",
  long_barrelled_pistol: "Long-barrelled Pistol",
  long_barrelled_revolver: "Long-barrelled Revolver",
};

const FILTERS = ["all", ...Object.keys(CATEGORY_LABELS)];
const FIREARM_TYPE_FILTERS = ["all", ...Object.keys(FIREARM_TYPE_LABELS)];
const PAGE_SIZE = 10;

const TITLE_HINT = "Make, Model and Calibre";

const DESCRIPTION_HINTS = {
  firearms: "Condition, extras included, and anything a buyer should know",
  ammunition: "Quantity, condition, and collection notes",
  parts: "What it fits, condition, and extras",
  accessories: "Condition, size if relevant, and extras",
  belts: "Size, condition, and what it is set up for",
  services: "What you offer, where, and any requirements",
};

function FieldHint({ id, children }) {
  return (
    <p id={id} className="form-hint">
      {children}
    </p>
  );
}

function paginationItems(current, total) {
  if (total <= 0) return [];
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({
      type: "page",
      page: i + 1,
    }));
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }
  if (current >= total - 2) {
    pages.add(total - 1);
    pages.add(total - 2);
    pages.add(total - 3);
  }
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const items = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p > prev + 1) {
      items.push({ type: "ellipsis", key: `e-${prev}-${p}` });
    }
    items.push({ type: "page", page: p });
    prev = p;
  }
  return items;
}

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 0 || total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const items = paginationItems(page, totalPages);
  return (
    <div className="for-sale-pagination-wrap">
      <p className="for-sale-pagination-status" aria-live="polite">
        Showing {from}–{to} of {total}
      </p>
      {totalPages > 1 ? (
        <nav className="for-sale-pagination" aria-label="Listings pages">
          <button
            type="button"
            className="for-sale-page-btn for-sale-page-nav"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            Previous
          </button>
          {items.map((item) =>
            item.type === "ellipsis" ? (
              <span key={item.key} className="for-sale-page-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item.page}
                type="button"
                className={`for-sale-page-btn${
                  item.page === page ? " active" : ""
                }`}
                aria-current={item.page === page ? "page" : undefined}
                aria-label={`Page ${item.page}`}
                onClick={() => onPage(item.page)}
              >
                {item.page}
              </button>
            )
          )}
          <button
            type="button"
            className="for-sale-page-btn for-sale-page-nav"
            disabled={page >= totalPages}
            onClick={() => onPage(page + 1)}
          >
            Next
          </button>
        </nav>
      ) : null}
    </div>
  );
}

function listingCategoryLabel(listing) {
  const cat = CATEGORY_LABELS[listing.category] || listing.category;
  if (listing.category === "firearms" && listing.firearm_type) {
    const kind =
      FIREARM_TYPE_LABELS[listing.firearm_type] || listing.firearm_type;
    return `${cat} · ${kind}`;
  }
  return cat;
}

const MAX_PHOTOS = 3;
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.82;

function formatPrice(listing) {
  if (listing.is_free) return "Free to a good home";
  const pounds = Number(listing.price_pence) / 100;
  return `£${pounds.toFixed(2)}`;
}

function parseUtcDatetime(value) {
  if (!value) return null;
  const s = String(value).trim();
  const iso = s.includes("T")
    ? s
    : `${s.replace(" ", "T")}${s.endsWith("Z") ? "" : "Z"}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatExpiry(value) {
  const d = parseUtcDatetime(value);
  if (!d) return value || "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function SellerMeta({ listing }) {
  return (
    <p className="muted">
      Listed by {listing.seller_first_name || "a member"}
      {listing.seller_email ? (
        <>
          {" · "}
          <a href={`mailto:${listing.seller_email}`}>{listing.seller_email}</a>
        </>
      ) : null}{" "}
      · expires {formatExpiry(listing.expires_at)}
    </p>
  );
}

async function compressImage(file) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose image files only");
  }
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Could not compress image"))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });

  const name = file.name.replace(/\.\w+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
}

function AuthImage({ src, alt, className }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl;
    fetch(`${API_BASE}${src}`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load image");
        return res.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (revoked) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!url) {
    return <div className={`for-sale-photo-placeholder ${className || ""}`} />;
  }
  return <img src={url} alt={alt} className={className} />;
}

// Fetches each photo once (auth blob -> object URL) so the gallery and
// lightbox can share the same full-size images without re-downloading.
function usePhotoUrls(photos) {
  const [urls, setUrls] = useState(() => (photos || []).map(() => null));

  useEffect(() => {
    let revoked = false;
    const objectUrls = [];
    setUrls((photos || []).map(() => null));
    (photos || []).forEach((p, i) => {
      fetch(`${API_BASE}${p.url}`, { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error("Failed to load image");
          return res.blob();
        })
        .then((blob) => {
          const u = URL.createObjectURL(blob);
          if (revoked) {
            URL.revokeObjectURL(u);
            return;
          }
          objectUrls.push(u);
          setUrls((prev) => prev.map((v, j) => (j === i ? u : v)));
        })
        .catch(() => {});
    });
    return () => {
      revoked = true;
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [photos]);

  return urls;
}

function PhotoLightbox({ urls, index, onClose, onNavigate }) {
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => setZoomed(false), [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onNavigate(index - 1);
      if (e.key === "ArrowRight" && index < urls.length - 1) {
        onNavigate(index + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, urls.length, onClose, onNavigate]);

  const url = urls[index];
  if (!url) return null;

  return (
    <div
      className="for-sale-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={onClose}
    >
      <button
        className="for-sale-lightbox-btn for-sale-lightbox-close"
        type="button"
        aria-label="Close photo viewer"
        onClick={onClose}
      >
        ×
      </button>
      {index > 0 ? (
        <button
          className="for-sale-lightbox-btn for-sale-lightbox-prev"
          type="button"
          aria-label="Previous photo"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index - 1);
          }}
        >
          ‹
        </button>
      ) : null}
      <div
        className={`for-sale-lightbox-stage${zoomed ? " zoomed" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={url}
          alt=""
          className="for-sale-lightbox-img"
          onClick={() => setZoomed((z) => !z)}
        />
      </div>
      {index < urls.length - 1 ? (
        <button
          className="for-sale-lightbox-btn for-sale-lightbox-next"
          type="button"
          aria-label="Next photo"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index + 1);
          }}
        >
          ›
        </button>
      ) : null}
      <div className="for-sale-lightbox-count">
        {index + 1} / {urls.length}
      </div>
    </div>
  );
}

function ContactForm({ listing }) {
  const [message, setMessage] = useState("");
  const [token, setToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError("Please complete the Turnstile challenge");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.forSaleContact(listing.id, message, token);
      setSent(true);
    } catch (err) {
      setToken(null);
      setTurnstileKey((k) => k + 1);
      setError(err.message || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="for-sale-contact">
        <h3>Contact seller</h3>
        <div className="message message-success" role="status">
          Message sent. The seller will reply using the contact details you
          included.
        </div>
      </div>
    );
  }

  return (
    <div className="for-sale-contact">
      <h3>Contact seller</h3>
      <p className="help-text">
        Your message about <strong>{listing.title}</strong> will be emailed to
        the seller. Their email address is not shown to you.
      </p>
      <div className="for-sale-contact-note">
        <strong>So the seller can reply,</strong> include one of the following
        in your message: your telephone number, your email address, or when you
        will next be at the club.
      </div>
      {error ? <div className="message message-error">{error}</div> : null}
      <form onSubmit={submit}>
        <div className="form-field">
          <label htmlFor="for-sale-contact-message">Message</label>
          <textarea
            id="for-sale-contact-message"
            required
            rows={5}
            maxLength={2000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <TurnstileField
          key={turnstileKey}
          onVerify={setToken}
          onExpire={() => setToken(null)}
        />
        <div className="inline-actions" style={{ marginTop: "1rem" }}>
          <button className="btn" type="submit" disabled={submitting}>
            {submitting ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ListingForm({ initial, onCancel, onSaved }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [category, setCategory] = useState(initial?.category || "accessories");
  const [firearmType, setFirearmType] = useState(initial?.firearm_type || "");
  const [isFree, setIsFree] = useState(!!initial?.is_free);
  const [price, setPrice] = useState(
    initial && !initial.is_free && initial.price_pence != null
      ? (Number(initial.price_pence) / 100).toFixed(2)
      : ""
  );
  const [photoFiles, setPhotoFiles] = useState([]);
  const [replacePhotos, setReplacePhotos] = useState(!initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onPickPhotos = async (e) => {
    const picked = Array.from(e.target.files || []).slice(0, MAX_PHOTOS);
    setError("");
    try {
      const compressed = [];
      for (const file of picked) {
        compressed.push(await compressImage(file));
      }
      setPhotoFiles(compressed);
      setReplacePhotos(true);
    } catch (err) {
      setError(err.message || "Could not process photos");
      setPhotoFiles([]);
      setReplacePhotos(!initial);
    }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (initial) {
        await api.forSalePatch(initial.id, {
          title,
          description,
          category,
          firearm_type: category === "firearms" ? firearmType : null,
          is_free: isFree,
          price: isFree ? undefined : price,
        });
        if (replacePhotos) {
          const fd = new FormData();
          photoFiles.forEach((f, i) => fd.append(`photo${i}`, f));
          await api.forSaleReplacePhotos(initial.id, fd);
        }
      } else {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("description", description);
        fd.append("category", category);
        if (category === "firearms") fd.append("firearm_type", firearmType);
        fd.append("is_free", isFree ? "1" : "0");
        if (!isFree) fd.append("price", price);
        photoFiles.forEach((f, i) => fd.append(`photo${i}`, f));
        await api.forSaleCreate(fd);
      }
      onSaved();
    } catch (err) {
      setError(err.message || "Could not save listing");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="for-sale-form" onSubmit={submit}>
      <h3>{initial ? "Edit listing" : "New listing"}</h3>
      {error ? <div className="message message-error">{error}</div> : null}
      <div className="form-field">
        <label htmlFor="for-sale-title">Title</label>
        <FieldHint id="for-sale-title-hint">{TITLE_HINT}</FieldHint>
        <input
          id="for-sale-title"
          required
          maxLength={120}
          placeholder={TITLE_HINT}
          aria-describedby="for-sale-title-hint"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="form-field">
        <label htmlFor="for-sale-category">Category</label>
        <FieldHint id="for-sale-category-hint">
          Choose the closest match for what you are listing
        </FieldHint>
        <select
          id="for-sale-category"
          value={category}
          aria-describedby="for-sale-category-hint"
          onChange={(e) => {
            const next = e.target.value;
            setCategory(next);
            if (next !== "firearms") setFirearmType("");
          }}
        >
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {category === "firearms" ? (
        <div className="form-field">
          <label htmlFor="for-sale-firearm-type">Firearm type</label>
          <FieldHint id="for-sale-firearm-type-hint">
            Section 1 or 2 shotgun, rifle, long-barrelled pistol or revolver
          </FieldHint>
          <select
            id="for-sale-firearm-type"
            required
            value={firearmType}
            aria-describedby="for-sale-firearm-type-hint"
            onChange={(e) => setFirearmType(e.target.value)}
          >
            <option value="">Select type</option>
            {Object.entries(FIREARM_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="form-field">
        <label htmlFor="for-sale-description">Description</label>
        <FieldHint id="for-sale-description-hint">
          {DESCRIPTION_HINTS[category] ||
            "Condition, extras included, and anything a buyer should know"}
        </FieldHint>
        <textarea
          id="for-sale-description"
          required
          rows={5}
          maxLength={4000}
          placeholder={
            DESCRIPTION_HINTS[category] ||
            "Condition, extras included, and anything a buyer should know"
          }
          aria-describedby="for-sale-description-hint"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="form-field">
        <label className="for-sale-checkbox">
          <input
            type="checkbox"
            checked={isFree}
            onChange={(e) => setIsFree(e.target.checked)}
          />
          Free to a good home
        </label>
      </div>
      {!isFree ? (
        <div className="form-field">
          <label htmlFor="for-sale-price">Price (£)</label>
          <FieldHint id="for-sale-price-hint">Asking price in pounds</FieldHint>
          <input
            id="for-sale-price"
            required
            inputMode="decimal"
            placeholder="0.00"
            aria-describedby="for-sale-price-hint"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </div>
      ) : null}
      <div className="form-field">
        <label htmlFor="for-sale-photos">
          Photos (up to {MAX_PHOTOS}
          {initial ? "; choose new files to replace" : ""})
        </label>
        <FieldHint id="for-sale-photos-hint">
          JPEG, PNG or WebP. Clear photos help listings sell.
        </FieldHint>
        <input
          id="for-sale-photos"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          aria-describedby="for-sale-photos-hint"
          onChange={onPickPhotos}
        />
        {photoFiles.length ? (
          <p className="muted">{photoFiles.length} photo(s) ready to upload</p>
        ) : initial?.photos?.length && !replacePhotos ? (
          <p className="muted">
            Keeping existing {initial.photos.length} photo(s)
          </p>
        ) : null}
      </div>
      <div className="inline-actions">
        <button className="btn" type="submit" disabled={submitting}>
          {submitting ? "Saving…" : initial ? "Save changes" : "List item"}
        </button>
        <button
          className="btn btn-secondary"
          type="button"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function HideActions({ listing, onSetHidden }) {
  if (!listing.can_hide) return null;
  return (
    <button
      className={`btn ${listing.is_hidden ? "btn-outline" : "btn-secondary"}`}
      type="button"
      onClick={() => onSetHidden(listing, !listing.is_hidden)}
    >
      {listing.is_hidden ? "Reinstate listing" : "Hide listing"}
    </button>
  );
}

function ListingCard({ listing, onView, onEdit, onDelete, onSetSold, onSetHidden }) {
  return (
    <article
      className={`for-sale-card${listing.is_sold ? " sold" : ""}${
        listing.is_hidden ? " hidden-listing" : ""
      }`}
    >
      <div className="for-sale-card-photos">
        {listing.photos?.length ? (
          listing.photos.map((p) => (
            <button
              key={p.id}
              type="button"
              className="for-sale-thumb-btn"
              onClick={() => onView(listing)}
              aria-label={`View details for ${listing.title}`}
            >
              <AuthImage src={p.url} alt="" className="for-sale-thumb" />
            </button>
          ))
        ) : (
          <div className="for-sale-photo-placeholder">No photo</div>
        )}
      </div>
      <div className="for-sale-card-body">
        <div className="for-sale-card-meta">
          <span className="for-sale-category">
            {listingCategoryLabel(listing)}
          </span>
          {listing.is_sold ? (
            <span className="for-sale-sold-badge">SOLD</span>
          ) : null}
          {listing.is_hidden ? (
            <span className="for-sale-hidden-badge">HIDDEN</span>
          ) : null}
        </div>
        <h3>
          <button
            type="button"
            className="for-sale-card-title"
            onClick={() => onView(listing)}
          >
            {listing.title}
          </button>
        </h3>
        <p className="for-sale-desc">
          <LinkifiedText text={listing.description} />
        </p>
        <SellerMeta listing={listing} />
        <div className="for-sale-card-footer">
          <div className="inline-actions">
            {!listing.is_owner ? (
              <button
                className="btn"
                type="button"
                onClick={() => onView(listing)}
              >
                Contact seller
              </button>
            ) : null}
            {listing.can_edit ? (
              <button
                className={`btn ${listing.is_sold ? "btn-outline" : "btn-sold"}`}
                type="button"
                onClick={() => onSetSold(listing, !listing.is_sold)}
              >
                {listing.is_sold ? "Mark as unsold" : "Mark as sold"}
              </button>
            ) : null}
            {listing.can_edit ? (
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => onEdit(listing)}
              >
                Edit
              </button>
            ) : null}
            {listing.can_delete ? (
              <button
                className="btn btn-danger"
                type="button"
                onClick={() => onDelete(listing)}
              >
                Delete
              </button>
            ) : null}
            <HideActions listing={listing} onSetHidden={onSetHidden} />
          </div>
          <span className="for-sale-price">{formatPrice(listing)}</span>
        </div>
      </div>
    </article>
  );
}

function ListingDetail({ listing, onBack, onEdit, onDelete, onSetSold, onSetHidden }) {
  const urls = usePhotoUrls(listing.photos);
  const [mainIndex, setMainIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  return (
    <div className="for-sale-detail">
      <button className="btn btn-secondary" type="button" onClick={onBack}>
        ← Back to listings
      </button>

      <div className="for-sale-detail-grid">
        <div className="for-sale-detail-photos">
          {urls.length ? (
            <>
              <button
                type="button"
                className="for-sale-detail-main"
                onClick={() => setLightboxIndex(mainIndex)}
                aria-label="View photo full size"
              >
                {urls[mainIndex] ? (
                  <img src={urls[mainIndex]} alt={listing.title} />
                ) : (
                  <div className="for-sale-photo-placeholder for-sale-detail-placeholder" />
                )}
              </button>
              {urls.length > 1 ? (
                <div className="for-sale-detail-thumbs">
                  {urls.map((u, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`for-sale-detail-thumb${
                        i === mainIndex ? " active" : ""
                      }`}
                      onClick={() => setMainIndex(i)}
                      aria-label={`Show photo ${i + 1}`}
                    >
                      {u ? <img src={u} alt="" /> : <span />}
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div className="for-sale-photo-placeholder for-sale-detail-placeholder">
              No photo
            </div>
          )}
        </div>

        <div className="for-sale-detail-body">
          {listing.is_sold ? (
            <div className="for-sale-sold-banner">SOLD</div>
          ) : null}
          {listing.is_hidden ? (
            <div className="for-sale-hidden-banner">
              Hidden from members
            </div>
          ) : null}
          <div className="for-sale-card-meta">
            <span className="for-sale-category">
              {listingCategoryLabel(listing)}
            </span>
            <span className="for-sale-price">{formatPrice(listing)}</span>
          </div>
          <h2>{listing.title}</h2>
          <p className="for-sale-desc">
            <LinkifiedText text={listing.description} />
          </p>
          <SellerMeta listing={listing} />
          {listing.can_edit || listing.can_delete || listing.can_hide ? (
            <div className="inline-actions">
              {listing.can_edit ? (
                <button
                  className={`btn ${
                    listing.is_sold ? "btn-outline" : "btn-sold"
                  }`}
                  type="button"
                  onClick={() => onSetSold(listing, !listing.is_sold)}
                >
                  {listing.is_sold ? "Mark as unsold" : "Mark as sold"}
                </button>
              ) : null}
              {listing.can_edit ? (
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => onEdit(listing)}
                >
                  Edit
                </button>
              ) : null}
              {listing.can_delete ? (
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={() => onDelete(listing)}
                >
                  Delete
                </button>
              ) : null}
              <HideActions listing={listing} onSetHidden={onSetHidden} />
            </div>
          ) : null}
        </div>
      </div>

      {!listing.is_owner && !listing.is_hidden ? (
        <ContactForm listing={listing} />
      ) : null}

      {lightboxIndex !== null ? (
        <PhotoLightbox
          urls={urls}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      ) : null}
    </div>
  );
}

export default function ForSaleTab() {
  const [category, setCategory] = useState("all");
  const [firearmType, setFirearmType] = useState("all");
  const [showHidden, setShowHidden] = useState(false);
  const [canModerate, setCanModerate] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [mode, setMode] = useState("list"); // list | create | edit | detail
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.forSaleList(
        category === "all" ? undefined : category,
        category === "firearms" && firearmType !== "all"
          ? firearmType
          : undefined,
        page,
        { hidden: showHidden }
      );
      setListings(data.listings || []);
      setPageSize(data.page_size || PAGE_SIZE);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setCanModerate(!!data.can_moderate);
      if (data.page && data.page !== page) setPage(data.page);
    } catch (err) {
      setListings([]);
      setTotal(0);
      setTotalPages(0);
      setError(err.message || "Failed to load listings");
    } finally {
      setLoading(false);
    }
  }, [category, firearmType, page, showHidden]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (listing) => {
    if (
      !window.confirm(
        `Delete “${listing.title}”? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await api.forSaleDelete(listing.id);
      setToast("Listing deleted");
      setMode("list");
      setViewing(null);
      load();
    } catch (err) {
      setError(err.message || "Could not delete listing");
    }
  };

  const handleSetSold = async (listing, sold) => {
    if (
      !window.confirm(
        sold
          ? `Mark “${listing.title}” as sold?`
          : `Mark “${listing.title}” as unsold?`
      )
    ) {
      return;
    }
    try {
      await api.forSaleSetSold(listing.id, sold);
      setToast(sold ? "Listing marked as sold" : "Listing marked as unsold");
      setViewing((v) =>
        v && v.id === listing.id ? { ...v, is_sold: sold } : v
      );
      load();
    } catch (err) {
      setError(err.message || "Could not update listing");
    }
  };

  const handleSetHidden = async (listing, hidden) => {
    if (
      !window.confirm(
        hidden
          ? `Hide “${listing.title}” from members? You can reinstate it later.`
          : `Reinstate “${listing.title}” so members can see it again?`
      )
    ) {
      return;
    }
    try {
      await api.forSaleSetHidden(listing.id, hidden);
      setToast(
        hidden ? "Listing hidden from members" : "Listing reinstated"
      );
      if (showHidden === hidden) {
        setViewing((v) =>
          v && v.id === listing.id ? { ...v, is_hidden: hidden } : v
        );
      } else {
        setMode("list");
        setViewing(null);
      }
      await load();
    } catch (err) {
      setError(err.message || "Could not update listing");
    }
  };

  return (
    <section className="members-tab-panel" aria-labelledby="for-sale-heading">
      <div className="for-sale-header">
        <div>
          <h2 id="for-sale-heading">For Sale</h2>
          <p className="help-text">
            Members can list items for up to 90 days. Contact sellers through
            the site — email addresses stay private from other members.
          </p>
        </div>
        {mode === "list" ? (
          <button
            className="btn"
            type="button"
            onClick={() => {
              setEditing(null);
              setMode("create");
            }}
          >
            List an item
          </button>
        ) : null}
      </div>

      <p className="for-sale-policy" role="note">
        The committee reserves the right to take down any listing without
        explanation.
      </p>

      {toast ? (
        <div className="message message-success" role="status">
          {toast}
        </div>
      ) : null}
      {error ? <div className="message message-error">{error}</div> : null}

      {mode === "detail" && viewing ? (
        <ListingDetail
          listing={viewing}
          onBack={() => {
            setMode("list");
            setViewing(null);
          }}
          onEdit={(item) => {
            setEditing(item);
            setMode("edit");
          }}
          onDelete={handleDelete}
          onSetSold={handleSetSold}
          onSetHidden={handleSetHidden}
        />
      ) : mode === "list" ? (
        <>
          <div
            className="for-sale-filters"
            role="group"
            aria-label="Filter by category"
          >
            {FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                className={`for-sale-filter${
                  category === key ? " active" : ""
                }`}
                onClick={() => {
                  setCategory(key);
                  if (key !== "firearms") setFirearmType("all");
                  setPage(1);
                }}
              >
                {key === "all" ? "All" : CATEGORY_LABELS[key]}
              </button>
            ))}
          </div>
          {category === "firearms" ? (
            <div
              className="for-sale-filters for-sale-filters-secondary"
              role="group"
              aria-label="Filter by firearm type"
            >
              {FIREARM_TYPE_FILTERS.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`for-sale-filter${
                    firearmType === key ? " active" : ""
                  }`}
                  onClick={() => {
                    setFirearmType(key);
                    setPage(1);
                  }}
                >
                  {key === "all" ? "All firearms" : FIREARM_TYPE_LABELS[key]}
                </button>
              ))}
            </div>
          ) : null}
          {canModerate ? (
            <div
              className="for-sale-filters for-sale-filters-secondary"
              role="group"
              aria-label="Committee listing visibility"
            >
              <button
                type="button"
                className={`for-sale-filter${showHidden ? "" : " active"}`}
                onClick={() => {
                  setShowHidden(false);
                  setPage(1);
                }}
              >
                Visible listings
              </button>
              <button
                type="button"
                className={`for-sale-filter${showHidden ? " active" : ""}`}
                onClick={() => {
                  setShowHidden(true);
                  setPage(1);
                }}
              >
                Hidden listings
              </button>
            </div>
          ) : null}

          {loading ? <p>Loading listings…</p> : null}
          {!loading && !listings.length ? (
            <p className="muted">
              {showHidden
                ? "No hidden listings."
                : "No listings in this category yet."}
            </p>
          ) : null}
          <div className="for-sale-list">
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                onView={(item) => {
                  setViewing(item);
                  setMode("detail");
                }}
                onEdit={(item) => {
                  setEditing(item);
                  setMode("edit");
                }}
                onDelete={handleDelete}
                onSetSold={handleSetSold}
                onSetHidden={handleSetHidden}
              />
            ))}
          </div>
          {!loading && total > 0 ? (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              pageSize={pageSize}
              onPage={setPage}
            />
          ) : null}
        </>
      ) : (
        <ListingForm
          initial={mode === "edit" ? editing : null}
          onCancel={() => {
            setMode("list");
            setEditing(null);
          }}
          onSaved={() => {
            setMode("list");
            setEditing(null);
            setToast(mode === "edit" ? "Listing updated" : "Listing created");
            if (mode !== "edit") setPage(1);
            load();
          }}
        />
      )}

    </section>
  );
}
